// src/pages/Issues/IssueDetail.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Paper,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Button,
  Breadcrumbs,
  Link
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import { useAuth } from '../../hooks/useAuth';
import { useDispatch } from 'react-redux';
import { getIssueById } from '../../store/slices/issueSlice';
import { toast } from 'react-toastify';
import { getStatusColor } from '../../components/Issues/utils';

const IssueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();
  
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const canViewIssue = hasPermission('issue-view');

  useEffect(() => {
    if (!canViewIssue) {
      toast.error('You do not have permission to view issues');
      navigate('/issues');
      return;
    }

    fetchIssueDetails();
  }, [id, canViewIssue]);

  const fetchIssueDetails = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getIssueById(id)).unwrap();
      const issueData = response?.data ?? response;
      if (response?.success || issueData) {
        setIssue(issueData);
      } else {
        setError('Failed to load issue details');
        toast.error('Failed to load issue details');
      }
    } catch (err) {
      console.error('Error fetching issue details:', err);
      setError(err.message || 'Failed to load issue details');
      toast.error(err.message || 'Failed to load issue details');
    } finally {
      setLoading(false);
    }
  };

  const getAssigneeName = () => {
    if (issue?.last_issue_log?.assignee) {
      return issue.last_issue_log.assignee.name;
    }
    return 'Not assigned';
  };

  const getStatusName = () => {
    if (issue?.last_issue_log?.status) {
      return issue.last_issue_log.status.name;
    }
    return 'Unknown';
  };

  const getAuthorName = () => {
    if (issue?.last_issue_log?.author) {
      return issue.last_issue_log.author.name;
    }
    return 'Unknown';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getUserInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!canViewIssue) {
    return (
      <PageLayout>
        <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            Access Denied - You don't have permission to view issues
          </Typography>
        </Container>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={60} />
      </PageLayout>
    );
  }

  if (error || !issue) {
    return (
      <PageLayout>
        <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {error || 'Issue not found'}
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/issues')}
            sx={{ mt: 2 }}
          >
            Back to Issues
          </Button>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link component={RouterLink} to="/issues" color="inherit">
              Issues
            </Link>
            <Typography color="text.primary">Issue Details</Typography>
          </Breadcrumbs>

          {/* Header with Back Button */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/issues')}
              sx={{ mb: 2 }}
            >
              Back to Issues
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* Left Column - Issue Details */}
            <Grid item xs={12} md={8}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {issue.title}
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Chip
                      label={getStatusName()}
                      color={getStatusColor(getStatusName())}
                      size="large"
                      sx={{ fontSize: '1rem', fontWeight: 600 }}
                    />
                  </Box>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" />
                    Description
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', minHeight: 100 }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {issue.description || 'No description provided'}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>

              {/* Timeline Section */}
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon color="primary" />
                    Activity Timeline
                  </Typography>
                  
                  {issue.issue_logs && issue.issue_logs.length > 0 ? (
                    <List sx={{ width: '100%' }}>
                      {issue.issue_logs.map((log, index) => (
                        <React.Fragment key={log.id}>
                          <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'primary.main' }}>
                                {getUserInitials(log.author?.name)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle1" component="span">
                                    {log.author?.name || 'Unknown User'}
                                  </Typography>
                                  <Chip
                                    label={log.status?.name || 'Unknown Status'}
                                    color={getStatusColor(log.status?.name)}
                                    size="small"
                                  />
                                  {log.assignee && (
                                    <Chip
                                      icon={<PersonIcon />}
                                      label={`Assigned to ${log.assignee.name}`}
                                      variant="outlined"
                                      size="small"
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarIcon fontSize="small" />
                                    {formatDate(log.created_at)}
                                  </Typography>
                                  {log.note && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                      Note: {log.note}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < issue.issue_logs.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      No activity history found
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column - Sidebar Information */}
            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    Issue Information
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Created By
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {getUserInitials(getAuthorName())}
                        </Avatar>
                        {getAuthorName()}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Current Assignee
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {getUserInitials(getAssigneeName())}
                        </Avatar>
                        {getAssigneeName()}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Model Type
                      </Typography>
                      <Typography variant="body1">
                        {issue.model_type || 'N/A'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Created Date
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" />
                        {formatDate(issue.created_at)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" />
                        {formatDate(issue.last_issue_log?.created_at)}
                      </Typography>
                    </Box>

                    {/* <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Issue ID
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {issue.id}
                      </Typography>
                    </Box> */}
                  </Box>
                </CardContent>
              </Card>

              {/* Quick Actions Card */}
              {/* <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={() => navigate(`/issues/edit/${issue.id}`)}
                    >
                      Edit Issue
                    </Button>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={() => navigate('/issues')}
                    >
                      Back to List
                    </Button>
                  </Box>
                </CardContent>
              </Card> */}
            </Grid>
          </Grid>
      </Container>
    </PageLayout>
  );
};

export default IssueDetail;